// src/components/cart/CartDrawer.jsx
import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { CartContext } from '../../context/CartContext';
import CheckoutModal from '../checkout/CheckoutModal';
import CartSuggestions from './Cartsuggestions';

const API = 'https://madadline.com/api';

/* ---------- Helpers ---------- */
const getEffectivePrice = (product) => {
  const original = Number(product?.prix ?? 0);
  let discount = Number(product?.discount ?? 0);
  const newPrice = product?.new_price != null ? Number(product.new_price) : null;
  if (discount > 0 && newPrice != null) return newPrice;
  if (discount > 0 && discount <= 1) discount *= 100;
  if (discount > 0 && original > 0) return Math.round(original * (1 - discount / 100));
  return original;
};

const getImageUrl = (image) => {
  if (!image || typeof image !== 'string') return '';
  const clean = image.trim();
  if (/^https?:\/\//i.test(clean)) return clean;
  const filename = clean.split('/').pop();
  if (!filename) return '';
  return `${API}/product-images/${encodeURIComponent(filename)}`;
};

// Le drawer charge lui-même son panier : plus besoin de props cartItems / subTotal.
function CartDrawer({ isOpen = false, onClose = () => {} }) {
  const { t } = useTranslation();
  const { updateCartItemCount } = useContext(CartContext) || {};

  const [cartItems, setCartItems] = useState([]);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);

  /* ---------- Chargement du panier à chaque ouverture ---------- */
  const loadCart = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('auth_token');
    try {
      let items = [];
      if (token) {
        const [cartRes, totalRes] = await Promise.all([
          axios.get(`${API}/cart`, { headers: { Authorization: `Bearer ${token}` } }),
          axios
            .get(`${API}/cart/total`, { headers: { Authorization: `Bearer ${token}` } })
            .catch(() => ({ data: { delivery_fee: 0 } })),
        ]);
        items = Array.isArray(cartRes.data) ? cartRes.data : [];
        setDeliveryFee(Number(totalRes.data?.delivery_fee || 0));
      } else {
        try {
          const parsed = JSON.parse(localStorage.getItem('cart') || '[]');
          items = Array.isArray(parsed) ? parsed : [];
        } catch {
          items = [];
        }
        setDeliveryFee(0);
      }
      setCartItems(items);
      if (typeof updateCartItemCount === 'function') updateCartItemCount(items);
    } catch (err) {
      console.error('Erreur chargement panier :', err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isOpen) loadCart();
  }, [isOpen, loadCart]);

  /* ---------- Blocage du scroll + touche Échap ---------- */
  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  /* ---------- Totaux ---------- */
  const totalItems = useMemo(
    () => cartItems.reduce((acc, i) => acc + Number(i.quantity || 0), 0),
    [cartItems]
  );

  const subTotal = useMemo(
    () =>
      cartItems.reduce(
        (acc, i) => acc + getEffectivePrice(i.product) * Number(i.quantity || 0),
        0
      ),
    [cartItems]
  );

  const totalSavings = useMemo(
    () =>
      cartItems.reduce((acc, i) => {
        const original = Number(i.product?.prix || 0);
        const effective = getEffectivePrice(i.product);
        return original > effective
          ? acc + (original - effective) * Number(i.quantity || 0)
          : acc;
      }, 0),
    [cartItems]
  );

  /* ---------- Synchronisation locale ---------- */
  const syncLocal = (updated) => {
    setCartItems(updated);
    localStorage.setItem('cart', JSON.stringify(updated));
    if (typeof updateCartItemCount === 'function') updateCartItemCount(updated);
  };

  /* ---------- Supprimer ---------- */
  const handleDelete = async (itemId) => {
    const token = localStorage.getItem('auth_token');
    setBusyId(itemId);
    try {
      if (token) {
        await axios.delete(`${API}/cart/item/${itemId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      syncLocal(cartItems.filter((i) => i.id !== itemId));
    } catch (err) {
      console.error('Erreur suppression :', err);
    } finally {
      setBusyId(null);
    }
  };

  /* ---------- Quantité ---------- */
  const handleQtyChange = async (itemId, newQty) => {
    if (newQty < 1) return;
    const item = cartItems.find((i) => i.id === itemId);
    if (!item) return;
    const token = localStorage.getItem('auth_token');
    setBusyId(itemId);
    try {
      if (token) {
        await axios.put(
          `${API}/cart/item/${itemId}`,
          { quantity: newQty, size: item.size },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      syncLocal(cartItems.map((i) => (i.id === itemId ? { ...i, quantity: newQty } : i)));
    } catch (err) {
      console.error('Erreur quantité :', err);
    } finally {
      setBusyId(null);
    }
  };

  /* ---------- Paiement réussi ---------- */
  const handlePaymentSuccess = () => {
    setShowCheckout(false);
    localStorage.removeItem('cart');
    setCartItems([]);
    setDeliveryFee(0);
    if (typeof updateCartItemCount === 'function') updateCartItemCount([]);
    onClose();
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={`cart-drawer-overlay ${isOpen ? 'is-open' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <aside
        className={`cart-drawer ${isOpen ? 'is-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={t('cart.heroTitle')}
        aria-hidden={!isOpen}
      >
        <header className="cart-drawer__head">
          <h2 className="cart-drawer__title">
            {t('cart.heroTitle')} <span>({totalItems})</span>
          </h2>
          <button className="cart-drawer__close" onClick={onClose} aria-label="Fermer">
            ✕
          </button>
        </header>

        {loading && cartItems.length === 0 ? (
          <div className="cart-drawer__loading">
            <div className="spinner" />
          </div>
        ) : cartItems.length === 0 ? (
          <div className="cart-drawer__empty">
            <div className="cart-drawer__empty-icon">🛍</div>
            <h3>{t('cart.emptyTitle')}</h3>
            <p>{t('cart.emptyText')}</p>
            <Link to="/boutique" className="cart-drawer__shop-btn" onClick={onClose}>
              {t('cart.discoverShop')}
            </Link>

            <CartSuggestions
              cartItems={[]}
              variant="drawer"
              enabled={isOpen}
              onNavigate={onClose}
            />
          </div>
        ) : (
          <>
            <div className="cart-drawer__items">
              {cartItems.map((item) => {
                const p = item.product || {};
                const img = getImageUrl(p.image);
                const name = p.nom || p.name || '';
                const price = getEffectivePrice(p);
                const originalPrice = Number(p.prix || 0);
                const hasPromo = originalPrice > price;
                const busy = busyId === item.id;

                return (
                  <article
                    key={item.id}
                    className={`cart-drawer__item ${busy ? 'is-busy' : ''}`}
                  >
                    <Link
                      to={`/product/${p.id}`}
                      className="cart-drawer__item-img"
                      onClick={onClose}
                    >
                      <img
                        src={img}
                        alt={name}
                        onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                      />
                    </Link>

                    <div className="cart-drawer__item-info">
                      <Link
                        to={`/product/${p.id}`}
                        className="cart-drawer__item-name"
                        onClick={onClose}
                      >
                        {name}
                      </Link>

                      <div className="cart-drawer__item-meta">
                        {item.size && <span>{t('cart.size')} : {item.size}</span>}
                        {item.color && <span>{t('cart.color')} : {item.color}</span>}
                      </div>

                      <div className="cart-drawer__item-price">
                        <span className="cart-drawer__item-current">
                          {price.toFixed(0)} DH
                        </span>
                        {hasPromo && (
                          <span className="cart-drawer__item-old">
                            {originalPrice.toFixed(0)} DH
                          </span>
                        )}
                      </div>

                      <div className="cart-drawer__item-actions">
                        <div className="cart-drawer__qty">
                          <button
                            onClick={() => handleQtyChange(item.id, Number(item.quantity) - 1)}
                            disabled={busy || Number(item.quantity) <= 1}
                            aria-label="-"
                          >
                            −
                          </button>
                          <span>{item.quantity}</span>
                          <button
                            onClick={() => handleQtyChange(item.id, Number(item.quantity) + 1)}
                            disabled={busy}
                            aria-label="+"
                          >
                            +
                          </button>
                        </div>

                        <button
                          className="cart-drawer__item-remove"
                          onClick={() => handleDelete(item.id)}
                          disabled={busy}
                          aria-label={t('cart.removeAria')}
                        >
                          🗑
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}

              <CartSuggestions
                cartItems={cartItems}
                variant="drawer"
                enabled={isOpen}
                onNavigate={onClose}
              />
            </div>

            <footer className="cart-drawer__footer">
              {totalSavings > 0 && (
                <div className="cart-drawer__row cart-drawer__row--save">
                  <span>{t('cart.youSave')}</span>
                  <span>-{totalSavings.toFixed(0)} DH</span>
                </div>
              )}

              <div className="cart-drawer__row">
                <span>{t('cart.subtotal')}</span>
                <span>{subTotal.toFixed(0)} DH</span>
              </div>

              <div className="cart-drawer__row">
                <span>{t('cart.shipping')}</span>
                <span>
                  {deliveryFee > 0 ? `${deliveryFee.toFixed(0)} DH` : t('cart.toCalculate')}
                </span>
              </div>

              <div className="cart-drawer__row cart-drawer__row--total">
                <span>{t('cart.total')}</span>
                <span>{(subTotal + deliveryFee).toFixed(0)} DH</span>
              </div>

              <button
                className="cart-drawer__checkout-btn"
                onClick={() => setShowCheckout(true)}
              >
                {t('cart.checkout')}
              </button>

              <Link to="/cart" className="cart-drawer__view-cart" onClick={onClose}>
                {t('cart.summaryTitle')} →
              </Link>
            </footer>
          </>
        )}
      </aside>

      {/* Checkout rapide (réutilise ton modal) */}
      {showCheckout && (
        <CheckoutModal
          cartItems={cartItems}
          subTotal={subTotal}
          deliveryFee={deliveryFee}
          onClose={() => setShowCheckout(false)}
          onSuccess={handlePaymentSuccess}
        />
      )}
    </>
  );
}

export default CartDrawer;